import { Dispatch, SetStateAction, useEffect } from 'react'

// ============================================
// useCalendlyOverlaySync — mobile overlay coordinator
// ============================================
// Owns the two mobile-only effects that previously lived inline in
// VoiceBotPanel.tsx around the Calendly booking flow:
//
//   1. When the Calendly embed opens on a mobile viewport, surface
//      it via the full-screen overlay.
//   2. After the user confirms a booking on mobile, auto-dismiss
//      the overlay 2 seconds later (long enough to show the success
//      state, short enough to feel snappy).
//
// Behavior is unchanged. The auto-dismiss timeout is now cleared on
// dep changes / unmount so a stale 2-second timer can never fire
// after the overlay has already been closed manually.

const AUTO_DISMISS_MS = 2000

export const useCalendlyOverlaySync = (
  isMobile: boolean,
  showCalendlyEmbed: boolean,
  bookingConfirmed: boolean,
  showCalendlyOverlay: boolean,
  setShowCalendlyOverlay: Dispatch<SetStateAction<boolean>>
): void => {
  // Sync overlay open state with embed-open state on mobile.
  useEffect(() => {
    if (isMobile && showCalendlyEmbed) {
      setShowCalendlyOverlay(true)
    }
  }, [isMobile, showCalendlyEmbed, setShowCalendlyOverlay])

  // Auto-dismiss overlay after booking confirmation on mobile.
  useEffect(() => {
    if (!(isMobile && bookingConfirmed && showCalendlyOverlay)) return
    const id = setTimeout(() => {
      setShowCalendlyOverlay(false)
    }, AUTO_DISMISS_MS)
    return () => clearTimeout(id)
  }, [isMobile, bookingConfirmed, showCalendlyOverlay, setShowCalendlyOverlay])
}
